import { useMemo } from 'react'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { SellDownloadPdfTemplateButton, SellDownloadButtonPDF, SellPdfPreview, SellViewWorkSheetButton, SellCreateWorkSheetButton, SellDownloadButtonCSV, SellQuickAccessButton, SellDownloadTags } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout/SellButtons'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { SellOrderDuplicateButton } from '../../../SellOrderButtons'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import { getAllWorkSheetGroupStatuses } from '../../../../../../../../../../../utils/reducers/getters'
import { OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'

export interface SellOrderFinishedStepButtonsProps extends OrderStepButtonsBaseComponentProps { }

const SellOrderFinishedStepButtons: React.FC<SellOrderFinishedStepButtonsProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useSellOrderFinishedStepButtonsLogic, ...rest }} className='mr-8' />
    )
}

const useSellOrderFinishedStepButtonsLogic = ({ data, onSubmit, isReadOnly }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    const name = 'sell_order_finished_step_buttons'
    const Order = data?.header
    const workSheets = getAllWorkSheetGroupStatuses()
    const hideSellQuickAccesButton = !Order?.QuoteOrderId && !Order?.SellOrderId && !Order?.SellDeliveryNoteId && !Order?.SellInvoiceId && !Order?.WorkSheetId && !Order?.relatedSellOrders?.length && !Order?.relatedSellDeliveryNotes?.length && !Order?.relatedSellInvoices?.length && !Order?.relatedWorkSheets?.length

    const defaultButtons = [{ "id": "previewSellOrder" }, { "id": "downloadSellOrder" }, { "id": "createWorkSheet" }, { "id": "viewWorkSheet" }, { "id": "quickAccess" }]

    const sellOrderDuplicateOrderText = t('duplicateOrder', 'Duplicar factura')
    const sellOrderDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const sellOrderPrintTagsText = t('printTags', 'Imprimir etiquetas')
    const sellViewWorkSheetText = t('goToWorkSheet', 'Ir a la hoja de trabajo')
    const sellCreateWorkSheetText = t('createWorkSheet', 'Crear hoja de trabajo')
    const sellOrderQuickAccessText = t('quickAccess', 'Acceso rápido')
    const sellOrderPreviewPDFText = t('previewPdf', 'Previsualizar PDF')
    const sellOrderDownloadPDFTemplateText = t('downloadSelectingPdfTemplate', 'Descargar seleccionando plantilla PDF')
    const sellOrderDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const sellOrderDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const sellOrderVariablesText = t('variables', 'Variables')
    const sellOrderProductTypesText = t('valueProductTypes', 'Valor tipos de producto')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateSell: {
                component: SellOrderDuplicateButton,
                extraProps: {
                    title: sellOrderDuplicateOrderText,
                    id: 'duplicateSell',
                    allRowData: data,
                }
            },
            downloadSellCsv: {
                component: SellDownloadButtonCSV,
                extraProps: {
                    title: sellOrderDownloadCSVText,
                    id: 'downloadSellCsv',
                    Order,
                    typeTemplate: 'sellOrder',
                }
            },
            downloadSellTag: {
                component: SellDownloadTags,
                extraProps: {
                    title: sellOrderPrintTagsText,
                    id: 'downloadSellTag',
                    Order,
                    typeTemplate: 'sellOrder',
                }
            },
            viewWorkSheet: {
                component: SellViewWorkSheetButton,
                showButton: Order?.WorkSheetId,
                extraProps: {
                    title: sellViewWorkSheetText,
                    id: 'viewWorkSheet',
                    WorkSheetId: Order?.WorkSheetId
                }
            },
            createWorkSheet: {
                component: SellCreateWorkSheetButton,
                showButton: !Order?.WorkSheetId && !!workSheets?.WorkSheetActiveStatusGroups?.length,
                extraProps: {
                    title: sellCreateWorkSheetText,
                    id: 'createWorkSheet',
                    Order,
                    createURL: '/api/app/order/sell/workSheet/createWorkSheetBySellOrder',
                    orderField: 'SellOrderId',
                    completeData: data
                }
            },
            quickAccess: {
                component: SellQuickAccessButton,
                showButton: !hideSellQuickAccesButton,
                extraProps: {
                    title: sellOrderQuickAccessText,
                    id: 'quickAccess',
                    Order,
                }
            },
            previewSellOrder: {
                component: SellPdfPreview,
                extraProps: {
                    title: sellOrderPreviewPDFText,
                    id: 'previewSellOrder',
                    Order,
                    typeTemplate: 'sellOrder',
                }
            },
            downloadSellOrderTemplate: {
                component: SellDownloadPdfTemplateButton,
                extraProps: {
                    title: sellOrderDownloadPDFTemplateText,
                    id: 'downloadSellOrderTemplate',
                    Order,
                    typeTemplate: 'sellOrder',
                    key: 'downloadSellOrderPDFTemplate'
                }
            },
            downloadSellOrder: {
                component: SellDownloadButtonPDF,
                extraProps: {
                    title: sellOrderDownloadPDFText,
                    id: 'downloadSellOrder',
                    Order,
                    typeTemplate: 'sellOrder',
                    key: 'downloadSellOrder'
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: sellOrderDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    isReadOnly,
                    onSubmit,
                }
            },
            variablesQuote: {
                component: OrderVariablesButton,
                extraProps: {
                    title: sellOrderVariablesText,
                    id: 'variablesQuote',
                    allRowData: data,
                    isReadOnly,
                    onSubmit,
                }
            },
            sellProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: sellOrderProductTypesText,
                    id: 'sellProductTypes',
                    allRowData: data,
                    onSubmit,
                    isReadOnly
                }
            },
        }
    }, [Order, data, hideSellQuickAccesButton, isReadOnly, onSubmit, sellCreateWorkSheetText, sellOrderDivideIntoChaptersText, sellOrderDownloadCSVText, sellOrderDownloadPDFTemplateText, sellOrderDownloadPDFText, sellOrderDuplicateOrderText, sellOrderPreviewPDFText, sellOrderPrintTagsText, sellOrderQuickAccessText, sellOrderVariablesText, sellViewWorkSheetText, workSheets?.WorkSheetActiveStatusGroups?.length, sellOrderProductTypesText])

    return { name, ButtonComponents, defaultButtons }
}

export const SellOrderFinishedStepButtonsNew: React.FC<SellOrderFinishedStepButtonsProps> = ({ data, onSubmit, ...rest }) => {
    return (
        <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useSellOrderFinishedStepButtonsLogic, ...rest }} className='mr-8' />
    )
}

export default SellOrderFinishedStepButtons